import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/utils/cn';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';
import ReadingChat from '../components/ReadingChat';
import ReadingSettingsMenu from '../components/ReadingSettingsMenu';
import MemoizedCardStack from '../components/MemoizedCardStack';
import DifficultyBadge from '../components/DifficultyBadge';
import { useCompleteSection, type CompleteSectionAnswerItem } from '../hooks/useCompleteSection';
import { useQuestions } from '../hooks/useQuestions';
import { getMe } from '@/services/authService';
import type { ReadingPage } from '../types';
import { markOnboardingSectionCompleted } from '@/utils/onboardingProgress';
import { analyticsService } from '@/services/analyticsService';
import { getNotificationAttribution } from '@/services/notificationAttribution';
import { CapacitorUtils } from '@/utils/capacitor';

type CardType = 'summary' | 'question';

interface UnifiedCard {
  type: CardType;
  id: string;
  content?: string;
  questionData?: {
    id: string;
    question: string;
    options: string[];
    correctAnswer: number;
    isReviewed?: boolean;
    difficulty?: 'easy' | 'medium' | 'hard' | string;
  };
}

interface ReadingState {
  bookId: number;
  bookTitle?: string;
  chapterId: number;
  sectionId: number;
  sectionTitle: string;
  summary: string;
  isBookAnalysis?: boolean;
  returnTo?: string;
  onboardingPreview?: boolean;
  source?: string;
  notificationCampaign?: string;
}

// A route element can be remounted by an auth/onboarding wrapper without a
// new navigation entry. Keep the initial splash decision outside the page
// instance so that remounting the same reading entry cannot replay frame one.
const initialReadingSplashShown = new Set<string>();

// تعریف مراحل راهنمای onboarding
const READING_GUIDE_STAGE = {
  INIT: 0, // ورود به صفحه
  CHAT_OPENED: 1, // باز شدن چت
  CHAT_SENT: 2, // ارسال پیام
  CHAT_RESPONSE: 3, // دریافت پاسخ
  FIRST_QUESTION: 4, // دیدن سوال اول
  SECOND_QUESTION_CORRECT: 5, // پایان با پاسخ درست
  SECOND_QUESTION_WRONG: 6, // پایان با پاسخ غلط
  REVIEW_QUESTION: 7, // نمایش سؤال مرور هوشمند
  CHAT_ERROR: 8, // خطا در چت
} as const;

const READING_ONBOARDING_GUIDE = {
  init: 'فقط نکات اصلی رو بخون.وقتت رو از سر راه نیاوردی که !',
  chatOpened: 'هر سوالی داری ازم درباره این قسمت بپرس',
  chatSending: 'صبر کن الان بهت جواب میدم',
  chatResponse: 'جوابتو گرفتی ؟ ادامه بده ',
  firstQuestion: 'یادگیری عمیق تر میخوای ؟ با سوال و جواب بهتر یاد بگیر',
  secondQuestionCorrect: 'داری میترکونی !',
  secondQuestionWrong: 'نباید جواب همه چیو بدونی که.',
  reviewQuestion: 'دیگه فراموش نمی‌کنی ! با سیستم مرور هوشمند',
  chatError: 'نمی‌دونم چی شد! ادامه بده ',
} as const;

const NONFICTION_READING_GUIDE_STEPS = [
  {
    id: 'nonfiction-reading-overview',
    title: 'مطالعه عمیق‌تر',
    description: 'قسمت‌های کتاب‌های غیرداستانی به صورت خلاصه همراه سوال هستند تا فقط ایده اصلی اون قسمت رو یاد بگیری. یعنی برای هر قسمت خلاصه رو می‌خونی و بعدش برای یادگیری عمیق‌تر سوالاتشو جواب می‌دی.',
  },
  {
    id: 'nonfiction-reading-controls',
    title: 'دستیار و تنظیمات مطالعه',
    description: 'روی صفحه بزن تا بتونی از تنظیمات تم رو تغییر بدی یا از دستیار بوکا هر سوالی درباره این قسمت داری بپرسی.',
  },
];

const NONFICTION_CHAT_GUIDE_STEPS = [
  {
    id: 'nonfiction-reading-chat-button',
    title: 'دستیار مطالعه بوکا',
    description: 'با کلیک روی این دکمه می‌تونی هر سوالی که مربوط به این قسمت از کتاب میشه رو از دستیار مطالعه بوکا بپرسی',
    target: '[data-guide="nonfiction-reading-chat-button"]',
  },
];

const NONFICTION_DIFFICULTY_GUIDE_STEPS = [
  {
    id: 'nonfiction-question-difficulty-badge',
    title: 'سطح سوال',
    description: 'هوش مصنوعی بوکا بر اساس جواب‌هایی که می‌دی برای هر کتاب بهت سوالاتی با سطح‌های مختلف و مخصوص به خودت رو می‌ده تا بهتر یاد بگیری.',
    target: '[data-guide="nonfiction-question-difficulty-badge"]',
  },
];

const NONFICTION_REVIEW_GUIDE_STEPS = [
  {
    id: 'nonfiction-review-question-badge',
    title: 'سوال مرور',
    description: 'الگوریتم هوشمند بوکا بر اساس جواب‌هایی که می‌دی، برای هر کتاب سوال‌های مرور‌دار نمایش می‌ده تا ایده‌های قبلی رو فراموش نکنی و برات یادآوری بشه.',
    target: '[data-guide="nonfiction-question-difficulty-badge"]',
  },
];

const SPLASH_DOT_DELAYS = [0.35, 0.65, 0.95] as const;
const SPLASH_DOT_DURATION = 0.2;
const SPLASH_READING_DELAY_MS = 350;
const SPLASH_READING_MS = 1150 + SPLASH_READING_DELAY_MS;

// Space reserved below onboarding cards for the fixed action button and a
// small visual gap. This is intentionally used only by onboarding cards.
const ReadingSplashOverlay = ({ text, onComplete }: { text: string; onComplete: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, SPLASH_READING_MS);
    return () => clearTimeout(timer);
  }, [onComplete]);

  const sizeClass = text.length > 42
    ? 'text-xl sm:text-2xl md:text-3xl'
    : text.length > 26
      ? 'text-2xl sm:text-3xl'
      : 'text-3xl sm:text-4xl';

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      className="absolute inset-0 z-[100] flex items-center justify-center backdrop-blur-xl bg-black/60 px-4 py-10"
      style={{ touchAction: 'none' }}
    >
      <div className={cn('text-white font-bold font-persian drop-shadow-2xl text-center leading-normal break-words max-w-[92vw]', sizeClass)} dir="rtl">
        <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          {text}
          <span className="inline-flex mr-1 font-sans text-4xl align-middle translate-y-[-4px]">
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SPLASH_DOT_DELAYS[0], duration: SPLASH_DOT_DURATION }}>.</motion.span>
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SPLASH_DOT_DELAYS[1], duration: SPLASH_DOT_DURATION }}>.</motion.span>
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SPLASH_DOT_DELAYS[2], duration: SPLASH_DOT_DURATION }}>.</motion.span>
          </span>
        </motion.span>
      </div>
    </motion.div>
  );
};

const BookReadingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const state = location.state as ReadingState | null;
  const isBookAnalysis = Boolean(state?.isBookAnalysis);
  const onboardingPreview = state?.onboardingPreview === true;
  const returnTo = state?.returnTo || '/learning/path';
  const bookId = state?.bookId;
  const sectionId = state?.sectionId;
  const summary = state?.summary || '';
  const splashEntryKey = `${location.key}:${sectionId ?? 'unknown'}:${isBookAnalysis ? 'analysis' : 'reading'}`;

  const logNonFictionChatClick = () => {
      void analyticsService.logEvent('nonfiction_chat_click', {
      page_context: 'read',
      flow: onboardingPreview ? 'onboarding' : 'normal',
      book_id: bookId ? String(bookId) : 'unknown',
      book_title: state?.bookTitle || 'unknown',
      ...(sectionId ? { section_id: String(sectionId) } : {}),
      });
  };


  const { questions, loading: questionsLoading, fetchQuestions } = useQuestions();

  useEffect(() => {
    if (!isBookAnalysis && sectionId) {
      // در onboarding فقط 2 سوال اول
      if (onboardingPreview) {
        fetchQuestions(sectionId);
      } else {
        fetchQuestions(sectionId);
      }
    }
  }, [isBookAnalysis, sectionId, fetchQuestions, onboardingPreview]);

  const summaryPages = useMemo<ReadingPage[]>(() => {
    if (!summary || !summary.trim()) return [];
    return [{
      id: `section-${sectionId}-summary`,
      pageNumber: 1,
      content: summary.trim()
    }];
  }, [summary, sectionId]);

  const error = !state ? 'داده های بخش یافت نشد' : (!summary ? 'خلاصه ای برای این بخش موجود نیست' : null);

  const completeSectionMutation = useCompleteSection();

  const [chatOpen, setChatOpen] = useState(false);
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [uiVisible, setUiVisible] = useState<boolean>(false);
  const [pageBgColor, setPageBgColor] = useState<'white' | 'sepia' | 'dark'>('white');
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [splashState, setSplashState] = useState<'intro' | 'questions' | 'none'>(() => {
    if (isBookAnalysis || initialReadingSplashShown.has(splashEntryKey)) return 'none';
    return 'intro';
  });
  const [showNonfictionGuide, setShowNonfictionGuide] = useState(false);
  const nonfictionGuideDelayRef = useRef<number | null>(null);
  const splashContextKeyRef = useRef<string | null>(null);
  const questionsSplashShownRef = useRef(false);

  useEffect(() => {
    const statusBarColor = pageBgColor === 'dark'
      ? '#111827'
      : pageBgColor === 'sepia'
        ? '#eadfc8'
        : '#ffffff';

    void CapacitorUtils.setStatusBarColor(statusBarColor);
    void CapacitorUtils.setStatusBarStyle(pageBgColor === 'dark' ? 'light' : 'dark');
  }, [pageBgColor]);

  useEffect(() => () => {
    void CapacitorUtils.setStatusBarColor('#ffffff');
    void CapacitorUtils.setStatusBarStyle('dark');
  }, []);
  const [answered, setAnswered] = useState<Record<string, number>>({});
  const [selectedOptions, setSelectedOptions] = useState<Record<string, number>>({});
  const [answers, setAnswers] = useState<CompleteSectionAnswerItem[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const sectionStartTrackedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!sectionId || isBookAnalysis || sectionStartTrackedRef.current === sectionId) return;
    sectionStartTrackedRef.current = sectionId;

    const notificationAttribution = getNotificationAttribution();
    const hasMatchingNotificationAttribution = notificationAttribution && (
      !notificationAttribution.targetBookId ||
      notificationAttribution.targetBookId === Number(bookId)
    );

    void analyticsService.logEvent('section_started', {
      section_id: String(sectionId),
      book_id: bookId ? String(bookId) : 'unknown',
      book_title: state?.bookTitle || 'unknown',
      content_type: 'non_fiction',
      page_context: 'read',
      flow: onboardingPreview ? 'onboarding' : 'normal',
      ...(state?.source === 'notification' || hasMatchingNotificationAttribution
        ? {
          source: 'notification',
          notification_campaign: state?.notificationCampaign || notificationAttribution?.campaign || 'unknown',
        }
        : {}),
    });
  }, [bookId, isBookAnalysis, sectionId, state?.bookTitle, state?.notificationCampaign, state?.source]);
  
  // Onboarding guide states
  const [guideStage, setGuideStage] = useState<number>(READING_GUIDE_STAGE.INIT);
  const guideStageRef = useRef<number>(READING_GUIDE_STAGE.INIT);
  
  const [summaryAccumulatedTime, setSummaryAccumulatedTime] = useState<number>(0);
  const summarySessionStartRef = useRef<number | null>(null);
  const [questionAccumulatedTime, setQuestionAccumulatedTime] = useState<Record<string, number>>({});
  const questionSessionStartRef = useRef<Record<string, number>>({});
  const [initialGems, setInitialGems] = useState<number>(0);
  const touchStartTimeRef = useRef<number>(0);
  const touchStartPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const touchHandledRef = useRef<boolean>(false);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
  const [onboardingCardHeight, setOnboardingCardHeight] = useState(0);
  const onboardingQuestionFontSizesRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (chatOpen) setHasOpenedChat(true);
  }, [chatOpen]);

  useEffect(() => {
    (async () => {
      try {
        const profile = await getMe();
        if (profile?.gems != null) {
          setInitialGems(profile.gems);
        }
      } catch (e) {
        console.warn('Failed to fetch initial gems', e);
      }
    })();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      onboardingQuestionFontSizesRef.current = {};
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goBack = useCallback(() => {
    if (onboardingPreview) {
      navigate('/onboarding/books', {
        replace: true,
        state: bookId ? { bookId, disableBookSwitcher: true, fromOnboarding: true } : undefined,
      });
      return;
    }
    navigate(-1);
  }, [navigate, onboardingPreview, bookId]);

  const setOnboardingGuide = useCallback((text: string, replay = false) => {
    if (!onboardingPreview) return;
    window.dispatchEvent(new CustomEvent('onboarding-read-guide', { detail: { text, replay } }));
  }, [onboardingPreview]);

  const setGuideWithStage = useCallback((stage: number, text: string, replay = false) => {
    if (!onboardingPreview) return;
    if (stage < guideStageRef.current) return; // فقط به جلو برویم
    guideStageRef.current = stage;
    setGuideStage(stage);
    setOnboardingGuide(text, replay);
  }, [onboardingPreview, setOnboardingGuide]);

  // مرحله 1: ورود به صفحه
  useEffect(() => {
    if (onboardingPreview && guideStage === READING_GUIDE_STAGE.INIT) {
      setGuideWithStage(READING_GUIDE_STAGE.INIT, READING_ONBOARDING_GUIDE.init);
    }
  }, [onboardingPreview, guideStage, setGuideWithStage]);

  // مرحله 3: باز شدن چت
  useEffect(() => {
    if (!onboardingPreview) return;
    if (chatOpen) {
      setGuideWithStage(READING_GUIDE_STAGE.CHAT_OPENED, READING_ONBOARDING_GUIDE.chatOpened);
    }
  }, [onboardingPreview, chatOpen, setGuideWithStage]);

  const toPersianDigits = useCallback((num: number): string => {
    return String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  }, []);

  const getOnboardingReadingFontSize = useCallback((content = '') => {
    if (!onboardingPreview) return 17;
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--onboarding-header-h')) || 110;
    const cardHeight = onboardingCardHeight > 0
      ? onboardingCardHeight
      : Math.max(220, windowHeight - headerHeight - 132);
    const cardWidth = Math.min(window.innerWidth * 0.92, 448) - 32;
    const availableHeight = Math.max(120, cardHeight - 40);
    const maxDeviceFont = Math.min(18, window.innerWidth * 0.05, windowHeight * 0.024);

    for (let fontPx = maxDeviceFont; fontPx >= 10; fontPx -= 0.25) {
      const charsPerLine = Math.max(16, Math.floor(cardWidth / (fontPx * 0.54)));
      const lineCount = content
        ? content.split(/\n/).reduce((total, line) => total + Math.max(1, Math.ceil(line.trim().length / charsPerLine)), 0)
        : 1;
      if (lineCount * fontPx * 1.95 <= availableHeight) return Number(fontPx.toFixed(2));
    }

    return 10;
  }, [onboardingCardHeight, onboardingPreview, windowHeight]);

  // Keep the complete onboarding question visible. The size is recalculated
  // from the viewport and the actual question/options so it adapts to phones.
  const getOnboardingQuestionFontSize = useCallback((question = '', options: string[] = []) => {
    if (!onboardingPreview) return 14;

    const cacheKey = `${question}\u0000${options.join('\u0001')}`;
    const cachedSize = onboardingQuestionFontSizesRef.current[cacheKey];
    if (cachedSize && onboardingCardHeight > 0) return cachedSize;

    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--onboarding-header-h'),
    ) || 110;
    const cardWidth = Math.min(window.innerWidth * 0.92, 448);
    const questionWidth = Math.max(180, cardWidth - 32);
    const optionWidth = Math.max(160, cardWidth - 52);
    const cardHeight = onboardingCardHeight > 0
      ? onboardingCardHeight
      : Math.max(220, windowHeight - headerHeight - 132);
    // Reserve the card padding, the title/options gap and a small safety
    // margin for font-metric differences between mobile browsers.
    const availableHeight = Math.max(100, (cardHeight - 50) * 0.95);

    for (let fontPx = Math.min(17.25, window.innerWidth * 0.0475, windowHeight * 0.0238); fontPx >= 9; fontPx -= 0.25) {
      // Deliberately conservative for Persian glyphs so a browser does not
      // wrap the last option to an extra line and clip it.
      const questionCharsPerLine = Math.max(12, Math.floor(questionWidth / (fontPx * 0.58)));
      const optionCharsPerLine = Math.max(10, Math.floor(optionWidth / (fontPx * 0.58)));
      const questionLineHeight = fontPx * 1.625; // leading-relaxed
      const optionLineHeight = fontPx * 1.375; // leading-snug
      const questionLines = Math.max(1, Math.ceil(question.trim().length / questionCharsPerLine));
      const optionsHeight = options.reduce((total, option) => {
        const optionLines = Math.max(1, Math.ceil(option.trim().length / optionCharsPerLine));
        return total + optionLines * optionLineHeight + 20;
      }, 0) + Math.max(0, options.length - 1) * 8;
      const totalHeight = questionLines * questionLineHeight + 12 + optionsHeight;

      if (totalHeight <= availableHeight) {
        const size = Number(fontPx.toFixed(2));
        if (onboardingCardHeight > 0) onboardingQuestionFontSizesRef.current[cacheKey] = size;
        return size;
      }
    }

    if (onboardingCardHeight > 0) onboardingQuestionFontSizesRef.current[cacheKey] = 9;
    return 9;
  }, [onboardingCardHeight, onboardingPreview, onboardingQuestionFontSizesRef, windowHeight]);

  const getCharLimit = useCallback((): number => {
    // محاسبه ارتفاع واقعی کارت برای خلاصه
    let cardMaxHeight: number;
    
    if (onboardingPreview) {
      // در onboarding: محاسبه فضای موجود برای کارت خلاصه
      const headerHeightStr = getComputedStyle(document.documentElement)
        .getPropertyValue('--onboarding-header-h');
      const headerHeight = headerHeightStr ? parseInt(headerHeightStr) : 128;
      
      // فضای موجود برای کارت خلاصه + دکمه + فاصله‌ها
      // ارتفاع کارت خلاصه = کل صفحه - هدر - دکمه - margin دکمه - فلش - فاصله پایین
      cardMaxHeight = onboardingCardHeight > 0
        ? onboardingCardHeight
        : windowHeight - headerHeight - 132;
    } else {
      // حالت عادی
      cardMaxHeight = windowHeight - 150;
    }
    
    // کم کردن padding داخلی کارت از ارتفاع
    const contentHeight = cardMaxHeight - (onboardingPreview ? 40 : 32);
    
    // محاسبه تعداد خطوط قابل نمایش - استفاده از کل فضای موجود
    const fontPx = onboardingPreview ? getOnboardingReadingFontSize() : 17;
    const lineHeight = fontPx * (onboardingPreview ? 1.9 : 2.2);
    const linesPerCard = Math.max(1, Math.floor(contentHeight / lineHeight));
    
    // محاسبه تعداد کاراکتر در هر خط - بهینه‌سازی حداکثری برای فارسی
    const cardWidth = Math.min(window.innerWidth * 0.88, 384) - (onboardingPreview ? 32 : 40);
    // Persian glyphs usually occupy less than half of the nominal font size.
    // Keep this estimate close to the real line width so the onboarding card
    // uses its available height instead of leaving a large empty area.
    const avgCharWidth = fontPx * 0.42;
    const charsPerLine = Math.max(24, Math.floor(cardWidth / avgCharWidth));
    
    // استفاده از 99.8% فضا برای پر کردن حداکثری کارت
    const totalChars = Math.floor(linesPerCard * charsPerLine * (onboardingPreview ? 0.96 : 0.88));
    
    console.log('📏 [getCharLimit]', {
      mode: onboardingPreview ? 'onboarding' : 'normal',
      windowHeight,
      cardMaxHeight,
      contentHeight,
      lineHeight,
      linesPerCard,
      charsPerLine,
      totalChars,
      fillRatio: '99.8%'
    });
    
    return totalChars;
  }, [getOnboardingReadingFontSize, onboardingCardHeight, windowHeight, onboardingPreview]);

  const splitSummaryByFontSize = useCallback((content: string, charLimit: number): string[] => {
    if (!content || !content.trim()) return [];
    
    // نرمال‌سازی محتوا: حذف فاصله‌های اضافی و خطوط خالی
    const normalizedContent = content.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
    
    // تقسیم به کلمات
    const sentences = normalizedContent.match(/[^.]+(?:\.|$)/g)?.map((sentence) => {
      const trimmed = sentence.trim();
      return /[.!؟؛:]$/.test(trimmed) ? trimmed : `${trimmed}.`;
    }).filter(Boolean)
      || [normalizedContent];
    const pages: string[] = [];
    let currentPage = '';
    let currentLength = 0;

    for (const sentence of sentences) {
      const sentenceLength = sentence.length;
      const separator = currentPage ? ' ' : '';
      const separatorLength = separator.length;
      const testLength = currentLength + separatorLength + sentenceLength;
      
      // اگر با اضافه کردن این کلمه از حد مجاز بگذریم و صفحه فعلی خالی نیست
      if (testLength > charLimit && currentPage.trim()) {
        pages.push(currentPage.trim());
        currentPage = sentence;
        currentLength = sentenceLength;
      } else {
        currentPage += separator + sentence;
        currentLength = testLength;
      }
    }

    // اضافه کردن آخرین صفحه
    if (currentPage.trim()) {
      pages.push(currentPage.trim());
    }

    console.log('📄 [splitSummary]', {
      totalSentences: sentences.length,
      charLimit,
      pagesCreated: pages.length,
      avgCharsPerPage: Math.round(pages.reduce((sum, p) => sum + p.length, 0) / pages.length),
      minChars: Math.min(...pages.map(p => p.length)),
      maxChars: Math.max(...pages.map(p => p.length))
    });

    return pages.length > 0 ? pages : [content];
  }, []);

  const summaryCardContents = useMemo(() => {
    const charLimit = getCharLimit();
    const allSummaryContent = summaryPages.map((p) => p.content).join('\n\n');
    return splitSummaryByFontSize(allSummaryContent, charLimit);
  }, [summaryPages, getCharLimit, splitSummaryByFontSize]);

  const unifiedCards = useMemo<UnifiedCard[]>(() => {
    const cards: UnifiedCard[] = [];
    const completeSummaryContent = summaryPages.map((page) => page.content).join('\n\n').trim();
    // Onboarding presents the whole section summary in one scrollable card.
    // Normal reading keeps the existing pagination based on the font size.
    const visibleSummaryCardContents = onboardingPreview
      ? (completeSummaryContent ? [completeSummaryContent] : [])
      : summaryCardContents;

    visibleSummaryCardContents.forEach((content, idx) => {
      cards.push({
        type: 'summary',
        id: `summary-${idx + 1}`,
        content,
      });
    });

    if (isBookAnalysis) {
      return cards;
    }

    if (!questionsLoading) {
      // در onboarding حداکثر دو سوال نمایش داده می‌شود؛ سؤال دوم مرور هوشمند است.
      const questionsToShow = onboardingPreview ? questions.slice(0, 2) : questions;
      
      questionsToShow.forEach((q) => {
        cards.push({
          type: 'question',
          id: `question-${q.id}`,
          questionData: {
            id: q.id,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            isReviewed: q.isReviewed,
            difficulty: q.difficulty,
          },
        });
      });
    } else {
      cards.push({
        type: 'question',
        id: 'shimmer-1',
      });
    }

    return cards;
  }, [summaryCardContents, summaryPages, questions, questionsLoading, isBookAnalysis, onboardingPreview]);

  const summaryCardCount = onboardingPreview ? Math.min(1, summaryCardContents.length) : summaryCardContents.length;

  const totalCards = unifiedCards.length;
  const hasQuestionsLoadedRef = useRef(false);

  useEffect(() => {
    if (!isBookAnalysis && !questionsLoading && questions.length > 0 && !hasQuestionsLoadedRef.current) {
      hasQuestionsLoadedRef.current = true;
      if (currentCardIndex >= summaryCardCount) {
        setCurrentCardIndex(summaryCardCount);
      }
    }
  }, [isBookAnalysis, questionsLoading, questions.length, currentCardIndex, summaryCardCount]);

  useEffect(() => {
    if (currentCardIndex >= totalCards && totalCards > 0) {
      setCurrentCardIndex(totalCards - 1);
    }
  }, [totalCards, currentCardIndex]);

  const currentCard = unifiedCards[currentCardIndex];
  const isQuestionCard = currentCard?.type === 'question';
  const currentQuestion = isQuestionCard ? currentCard.questionData : null;
  const currentSelectedOption = currentQuestion ? selectedOptions[currentQuestion.id] : undefined;
  const handleIntroSplashComplete = useCallback(() => {
    setSplashState('none');
    if (!onboardingPreview && !isBookAnalysis) {
      if (nonfictionGuideDelayRef.current !== null) {
        window.clearTimeout(nonfictionGuideDelayRef.current);
      }
      nonfictionGuideDelayRef.current = window.setTimeout(() => {
        setShowNonfictionGuide(true);
        nonfictionGuideDelayRef.current = null;
      }, 650);
    }
  }, [isBookAnalysis, onboardingPreview]);

  const handleQuestionsSplashComplete = useCallback(() => {
    setSplashState('none');
  }, []);

  useEffect(() => {
    return () => {
      if (nonfictionGuideDelayRef.current !== null) {
        window.clearTimeout(nonfictionGuideDelayRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const splashContextKey = JSON.stringify([isBookAnalysis, sectionId]);
    if (splashContextKeyRef.current === splashContextKey) return;
    if (splashContextKeyRef.current === null) {
      splashContextKeyRef.current = splashContextKey;
      initialReadingSplashShown.add(splashEntryKey);
      return;
    }
    splashContextKeyRef.current = splashContextKey;

    setSplashState(isBookAnalysis || initialReadingSplashShown.has(splashEntryKey) ? 'none' : 'intro');
    initialReadingSplashShown.add(splashEntryKey);
    setShowNonfictionGuide(false);
    setCurrentCardIndex(0);
    questionsSplashShownRef.current = false;
  }, [isBookAnalysis, sectionId, splashEntryKey]);

  useEffect(() => {
    if (isBookAnalysis || questionsLoading || !currentCard) return;

    const now = Date.now();
    const isSummaryCard = currentCard.type === 'summary';
    const currentQuestionId = currentCard.type === 'question' ? currentCard.questionData?.id ?? null : null;

    if (isSummaryCard) {
      Object.keys(questionSessionStartRef.current).forEach((qId) => {
        const sessionStart = questionSessionStartRef.current[qId];
        if (sessionStart) {
          setQuestionAccumulatedTime((prev) => ({
            ...prev,
            [qId]: (prev[qId] || 0) + (now - sessionStart)
          }));
          delete questionSessionStartRef.current[qId];
        }
      });

      if (summarySessionStartRef.current === null) {
        summarySessionStartRef.current = now;
      }
    } else if (currentQuestionId) {
      if (summarySessionStartRef.current !== null) {
        const sessionTime = now - summarySessionStartRef.current;
        setSummaryAccumulatedTime((prev) => prev + sessionTime);
        summarySessionStartRef.current = null;
      }

      Object.keys(questionSessionStartRef.current).forEach((otherQId) => {
        if (otherQId !== currentQuestionId) {
          const sessionStart = questionSessionStartRef.current[otherQId];
          if (sessionStart) {
            setQuestionAccumulatedTime((prev) => ({
              ...prev,
              [otherQId]: (prev[otherQId] || 0) + (now - sessionStart)
            }));
            delete questionSessionStartRef.current[otherQId];
          }
        }
      });

      if (answered[currentQuestionId] === undefined && !questionSessionStartRef.current[currentQuestionId]) {
        questionSessionStartRef.current[currentQuestionId] = now;
      }
    }
  }, [isBookAnalysis, currentCard, questionsLoading, answered]);

  // مرحله 7: ورود به تنها سوال onboarding
  useEffect(() => {
    if (!onboardingPreview || isBookAnalysis) return;
    if (currentCard?.type === 'question' && guideStage < READING_GUIDE_STAGE.FIRST_QUESTION) {
      setGuideWithStage(READING_GUIDE_STAGE.FIRST_QUESTION, READING_ONBOARDING_GUIDE.firstQuestion);
    }
  }, [onboardingPreview, currentCard, guideStage, isBookAnalysis, setGuideWithStage]);

  useEffect(() => {
    if (!onboardingPreview || isBookAnalysis || currentCard?.type !== 'question') return;
    const questionIndex = currentCardIndex - summaryCardCount;
    if (questionIndex === 1 && currentCard.questionData) {
      setGuideWithStage(READING_GUIDE_STAGE.REVIEW_QUESTION, READING_ONBOARDING_GUIDE.reviewQuestion);
    }
  }, [onboardingPreview, isBookAnalysis, currentCard, currentCardIndex, summaryCardCount, setGuideWithStage]);

  // مرحله 8-11: پاسخ به سوالات
  useEffect(() => {
    if (!onboardingPreview || isBookAnalysis) return;
    if (currentCard?.type !== 'question' || !currentCard.questionData) return;
    
    const qId = currentCard.questionData.id;
    const userAnswer = answered[qId];
    const correctAnswer = currentCard.questionData.correctAnswer;
    
    if (userAnswer === undefined) return;
    
    const isCorrect = userAnswer === correctAnswer;
    if (guideStage === READING_GUIDE_STAGE.FIRST_QUESTION) {
      if (isCorrect) {
        setGuideWithStage(READING_GUIDE_STAGE.SECOND_QUESTION_CORRECT, READING_ONBOARDING_GUIDE.secondQuestionCorrect);
      } else {
        setGuideWithStage(READING_GUIDE_STAGE.SECOND_QUESTION_WRONG, READING_ONBOARDING_GUIDE.secondQuestionWrong);
      }
    }
  }, [onboardingPreview, currentCard, answered, guideStage, isBookAnalysis, currentCardIndex, summaryCardCount, setGuideWithStage]);

  const onConfirmAnswer = useCallback(() => {
    if (!currentQuestion || currentSelectedOption === undefined) return;

    const qId = currentQuestion.id;
    const now = Date.now();

    let totalTime = questionAccumulatedTime[qId] || 0;
    if (questionSessionStartRef.current[qId]) {
      totalTime += (now - questionSessionStartRef.current[qId]);
      delete questionSessionStartRef.current[qId];
    }

    const timeSpentSeconds = Math.max(0, Math.floor(totalTime / 1000));

    setAnswered((prev) => ({ ...prev, [qId]: currentSelectedOption }));
    setAnswers((prev) => [
      ...prev,
      {
        question_id: qId,
        selected_option_id: currentSelectedOption + 1,
        time_spent: timeSpentSeconds
      }
    ]);
  }, [currentQuestion, currentSelectedOption, questionAccumulatedTime]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;

    if (isBookAnalysis) {
      navigate(returnTo, {
        replace: true,
        state: bookId ? { bookId } : undefined,
      });
      return;
    }

    if (!sectionId) return;

    try {
      setSubmitting(true);

      let finalSummaryTime = summaryAccumulatedTime;
      if (summarySessionStartRef.current !== null) {
        finalSummaryTime += (Date.now() - summarySessionStartRef.current);
      }

      const reading_duration = Math.max(0, Math.floor(finalSummaryTime / 1000));

      const res = await completeSectionMutation.mutateAsync({
        section_id: sectionId,
        reading_duration,
        answers,
      });

      const notificationAttribution = getNotificationAttribution();
      const hasMatchingNotificationAttribution = (state?.source === 'notification' || notificationAttribution) && (
        !notificationAttribution?.targetBookId ||
        notificationAttribution.targetBookId === Number(res.book_id)
      );

      void analyticsService.logEvent('section_completed', {
        section_id: String(sectionId),
        book_id: String(res.book_id),
        book_title: res.book_title,
        book_category: res.book_category,
        reading_duration,
        content_type: res.content_type,
        flow: onboardingPreview ? 'onboarding' : 'normal',
        ...(state?.source === 'notification' || hasMatchingNotificationAttribution
          ? {
            source: 'notification',
            notification_campaign: state?.notificationCampaign || notificationAttribution?.campaign || 'unknown',
            ...(notificationAttribution?.targetBookId
              ? { notification_book_id: String(notificationAttribution.targetBookId) }
              : {}),
          }
          : {}),
      });

      if (onboardingPreview) markOnboardingSectionCompleted();

      // Invalidate all related queries after successful section completion
      // This ensures fresh data when user returns to learning path
      if (bookId) {
        // Invalidate learning path data
        await queryClient.invalidateQueries({ queryKey: ['my-sections', bookId] });
        
        // Invalidate library data
        await queryClient.invalidateQueries({ queryKey: ['user-library'] });
        await queryClient.invalidateQueries({ queryKey: ['book-detail', bookId] });
      }

      navigate(onboardingPreview ? '/onboarding/xp-claim' : '/learning/xp-claim', {
        replace: true,
        state: {
          points: res.points,
          time: res.time,
          success_percentage: res.success_percentage,
          message: res.message,
          total_questions: questions.length,
          streak: res.streak,
          streak_days: res.streak_days,
          previous_streak: res.previous_streak,
          previous_level: res.previous_level,
          previous_level_xp: res.previous_level_xp,
          previous_next_level_xp: res.previous_next_level_xp,
          level: res.level,
          current_level_xp: res.current_level_xp,
          next_level_xp: res.next_level_xp,
          level_up: res.level_up,
          received_gems: res.received_gems,
          received_xp: res.received_xp,
          daily_goal: res.daily_goal,
          streak_goal: res.streak_goal,
          daily_goal_gems: res.daily_goal_gems,
          daily_goal_xp: res.daily_goal_xp,
          streak_goal_gems: res.streak_goal_gems,
          streak_goal_xp: res.streak_goal_xp,
          today_minutes: res.today_minutes,
          previous_minutes: res.previous_minutes,
          initialGems,
        }
      });
    } catch (e) {
      console.error('Submit section failed', e);
      alert('خطا در ارسال بخش');
      navigate(onboardingPreview ? '/onboarding/xp-claim' : '/learning/xp-claim', { replace: true });
    } finally {
      setSubmitting(false);
    }
  }, [answers, sectionId, submitting, navigate, completeSectionMutation, summaryAccumulatedTime, initialGems, questions.length, isBookAnalysis, returnTo, bookId, onboardingPreview]);

  const customAskFunction = useCallback(async (_: number, question: string): Promise<string> => {
    return `پاسخ به: ${question}`;
  }, []);

  const currentPageContent = useMemo(() => {
    if (currentCard?.type === 'summary') {
      return currentCard.content || '';
    }
    return '';
  }, [currentCard]);

  const paginate = useCallback((newDirection: number) => {
    const newIndex = currentCardIndex + newDirection;
    if (newIndex >= 0 && newIndex < totalCards) {
      if (newDirection > 0) {
        const cardData = unifiedCards[currentCardIndex];
        if (cardData?.type === 'question' && cardData.questionData) {
          const qId = cardData.questionData.id;
          if (answered[qId] === undefined) return;
        }
      }
      setCurrentCardIndex(newIndex);
      if (
        newDirection > 0 &&
        currentCardIndex < summaryCardCount &&
        newIndex >= summaryCardCount &&
        !questionsSplashShownRef.current
      ) {
        questionsSplashShownRef.current = true;
        setSplashState('questions');
      }
    }
  }, [currentCardIndex, totalCards, unifiedCards, answered, summaryCardCount]);

  const onDragEnd = useCallback((_ : unknown, { offset }: PanInfo) => {
    const swipeConfidenceThreshold = 40;
    const swipe = offset.y;
    if (swipe < -swipeConfidenceThreshold) paginate(1);
    else if (swipe > swipeConfidenceThreshold) paginate(-1);
  }, [paginate]);

  const cardBgClasses = {
    white: 'bg-[#6e61ff] text-white',
    sepia: 'bg-[#8B5A2B] text-[#f4ecd8]',
    dark: 'bg-[#3b3a5c] text-gray-100'
  };

  const activeQuestionActionClass = pageBgColor === 'dark'
    ? 'bg-white text-[#1f2937] active:scale-95'
    : pageBgColor === 'sepia'
      ? 'bg-[#8B5A2B] text-white active:scale-95'
      : 'bg-[#6e61ff] text-white active:scale-95';

  const disabledQuestionActionClass = pageBgColor === 'dark'
    ? 'disabled:bg-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed'
    : 'disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed';

  const lastQuestionDisabledActionClass = pageBgColor === 'dark'
    ? 'disabled:bg-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed'
    : 'disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed';

  const onboardingDisabledQuestionActionClass = 'disabled:bg-slate-200 disabled:text-slate-500 disabled:border disabled:border-slate-300 disabled:opacity-100 disabled:cursor-not-allowed';

  const handleOnboardingSummaryContinue = useCallback(() => {
    if (!hasOpenedChat) {
      setUiVisible(false);
      logNonFictionChatClick();
      setChatOpen(true);
      return;
    }

    if (!questionsLoading && questions.length === 0) {
      setChatOpen(false);
      void handleSubmit();
      return;
    }

    // Once the chat has been opened, the onboarding flow can continue even
    // when the user did not receive an answer or the request failed.
    setChatOpen(false);
    setCurrentCardIndex(summaryCardCount);
    if (!questionsSplashShownRef.current) {
      questionsSplashShownRef.current = true;
      setSplashState('questions');
    }
  }, [handleSubmit, hasOpenedChat, logNonFictionChatClick, questions.length, questionsLoading, summaryCardCount]);

  const renderCards = () => {
    const cards = [];

    for (let i = totalCards - 1; i >= 0; i--) {
      const card = unifiedCards[i];
      const diff = i - currentCardIndex;
      if (Math.abs(diff) > 1) continue;

      const isCurrent = diff === 0;
      const opacity = isCurrent ? 1 : 0.6;
      const scale = isCurrent ? 1 : 0.85;
      const yOffset = diff * 110;
      const zIndex = 50 - Math.abs(diff);
      const filterBlur = isCurrent ? '0px' : '6px';
      const isLastCard = i === totalCards - 1;

      cards.push(
        <motion.div
          key={card.id + i}
          className={cn(
            "absolute flex flex-col items-center",
            onboardingPreview 
              ? "w-[92%] sm:w-[88%] max-w-md" 
              : "w-[88%] sm:w-[85%] max-w-sm sm:max-w-md"
          )}
          initial={{ y: yOffset + (diff > 0 ? 50 : -50), opacity: 0, scale: scale * 0.9 }}
          animate={{ y: yOffset, opacity, scale }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          drag={isCurrent && !onboardingPreview ? 'y' : false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.8}
          onDragStart={isCurrent ? () => { setUiVisible(false); } : undefined}
          onDragEnd={isCurrent ? onDragEnd : undefined}
          style={{
            zIndex,
            filter: filterBlur,
            touchAction: isCurrent && !onboardingPreview ? 'none' : 'auto',
            pointerEvents: isCurrent ? 'auto' : 'none',
            margin: '0 auto',
            // The card area has 18px top and 10px bottom padding. Subtract
            // those from the absolute card height so it cannot enter the
            // action row on mobile browsers that resolve 100% against the
            // padding box.
            height: onboardingPreview ? 'calc(100% - 28px)' : undefined,
            // Preserve blur/scale visuals while keeping the card on a stable
            // compositor layer during a swipe.
            willChange: 'transform, opacity, filter',
            backfaceVisibility: 'hidden',
          }}
        >
          <div
            className={cn(
            'w-full rounded-2xl shadow-2xl flex flex-col',
            cardBgClasses[pageBgColor],
            onboardingPreview && 'overflow-hidden',
            isCurrent && 'current-reading-card'
            )}
            style={{ 
              minHeight: onboardingPreview 
                ? '100%'
                : 'auto',
              maxHeight: onboardingPreview 
                ? '100%'
                : (card.type === 'summary'
                    ? 'calc(100dvh - max(2.25rem, calc(1rem + env(safe-area-inset-top) + env(safe-area-inset-bottom))))'
                    : 'calc(100dvh - max(3rem, calc(2rem + env(safe-area-inset-top) + env(safe-area-inset-bottom))))'),
              height: onboardingPreview
                ? '100%'
                : undefined
            }}
          >
            {card.type === 'summary' ? (
              <div className={cn(
                "w-full h-full flex flex-col",
                onboardingPreview ? "px-4 py-5" : "px-5 py-4"
              )}>
                <div className={cn(
                  "w-full flex items-start",
                    onboardingPreview
                    ? "flex-1 min-h-0 overflow-y-auto"
                    : "h-full overflow-hidden"
                )}
                  onTouchStart={handleOnboardingTextTouchStart}
                  onTouchMove={handleOnboardingTextTouchMove}
                  onTouchEnd={handleOnboardingTextTouchEnd}
                  onTouchCancel={handleOnboardingTextTouchEnd}
                  style={onboardingPreview ? { touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' } : undefined}>
                  <p className={cn(
                    "w-full text-justify font-persian whitespace-pre-wrap break-words drop-shadow-sm font-medium [text-align-last:right]",
                    onboardingPreview ? "text-[16px] leading-[2.25rem]" : "text-[17px] leading-[2.2rem]"
                  )}
                    style={onboardingPreview ? {
                      // Use the normal onboarding reading size and let the
                      // card scroll instead of shrinking the full summary
                      // down until it technically fits.
                      fontSize: `${getOnboardingReadingFontSize()}px`,
                      lineHeight: `${getOnboardingReadingFontSize() * 1.95}px`,
                    } : undefined}
                  >
                    {card.content}
                  </p>
                </div>
              </div>
            ) : !card.questionData ? (
              <div className="w-full px-5 py-5">
                <div className="w-full h-40 animate-pulse bg-white/20 rounded-xl" />
              </div>
            ) : (
              <div className={cn(
                "w-full flex flex-col",
                onboardingPreview ? "px-3 py-4 min-h-0 flex-1" : "px-3 py-4",
                !onboardingPreview && "overflow-hidden"
              )} onClick={(e) => e.stopPropagation()}>
                <div
                  dir="rtl"
                  className="mb-2 flex w-full justify-start"
                >
                  <DifficultyBadge
                    difficulty={card.questionData.difficulty}
                    isReview={card.questionData.isReviewed || (onboardingPreview && i - summaryCardCount === 1)}
                    reviewLabel={onboardingPreview && i - summaryCardCount === 1 ? 'مرور هوشمند' : undefined}
                    fontSize={onboardingPreview
                      ? getOnboardingQuestionFontSize(card.questionData.question, card.questionData.options)
                      : undefined}
                    guideTargetId={isCurrent ? 'nonfiction-question-difficulty-badge' : undefined}
                  />
                </div>
                <h3 className={cn(
                  "font-bold font-persian text-justify leading-relaxed flex-shrink-0 px-1",
                  onboardingPreview ? "text-[15px]" : "text-[15.5px]"
                )}
                  style={onboardingPreview ? {
                  fontSize: `${getOnboardingQuestionFontSize(card.questionData.question, card.questionData.options)}px`,
                  } : undefined}
                >
                  {card.questionData.question}
                </h3>
                <div 
                  className={cn(
                    "flex flex-col mt-3 min-h-0 flex-1 overflow-hidden",
                    !onboardingPreview && "overflow-hidden"
                  )}
                  style={{
                    maxHeight: onboardingPreview ? undefined : 'calc(100vh - 280px)' // حالت عادی: فضا برای همه المان‌ها
                  }}
                >
                  <div 
                    className={cn(
                      "min-h-0 flex-1 flex flex-col space-y-2",
                      "overflow-y-auto hide-scrollbar scroll-smooth",
                      !onboardingPreview && "pb-3"
                    )}
                    style={{ 
                      touchAction: 'pan-y',
                      overscrollBehavior: 'contain',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    {card.questionData.options.map((option, optIdx) => {
                      const qId = card.questionData!.id;
                      const hasAnswered = answered[qId] !== undefined;
                      const isCorrect = card.questionData!.correctAnswer === optIdx;
                      const isSelected = selectedOptions[qId] === optIdx;
                      const isAnsweredOption = answered[qId] === optIdx;

                      return (
                        <div key={optIdx} className={cn(optIdx > 0 ? "mt-2" : "", onboardingPreview && "shrink-0")}>
                          <button
                            onClick={() => !hasAnswered && setSelectedOptions((prev) => ({ ...prev, [qId]: optIdx }))}
                            disabled={hasAnswered}
                            className={cn(
                              'w-full rounded-xl text-justify transition-all font-persian leading-snug break-words',
                              'select-none active:scale-[0.98]',
                              onboardingPreview && 'shrink-0',
                              onboardingPreview && 'border-2 box-border',
                              onboardingPreview ? 'py-2.5 px-3.5 text-[13.5px]' : 'py-1.5 px-2.5 text-[13.5px]',
                              hasAnswered && isCorrect && 'bg-green-500/40 border-2 border-green-400',
                              hasAnswered && isAnsweredOption && !isCorrect && 'bg-red-500/40 border-2 border-red-400',
                              !hasAnswered && isSelected && 'bg-white/30 border-2 border-white',
                              !isSelected && !hasAnswered && 'bg-white/10 border border-white/20',
                              hasAnswered && !isCorrect && !isAnsweredOption && 'opacity-50 bg-white/5'
                            )}
                            style={{
                              touchAction: 'manipulation',
                              boxSizing: onboardingPreview ? 'border-box' : undefined,
                              borderWidth: onboardingPreview ? '2px' : undefined,
                              fontSize: onboardingPreview
                                ? `${getOnboardingQuestionFontSize(card.questionData!.question, card.questionData!.options)}px`
                                : undefined,
                            }}
                          >
                            {option}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {isCurrent && !onboardingPreview && card.type === 'question' && card.questionData && (
            <div className={cn(
              "w-[85%]",
              onboardingPreview ? "mt-2" : "mt-2"
            )}>
              {answered[card.questionData.id] === undefined ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onConfirmAnswer(); }}
                  disabled={selectedOptions[card.questionData.id] === undefined}
                  className={cn(
                    'w-full rounded-xl font-bold font-persian transition-all shadow-lg',
                    onboardingPreview ? 'py-2.5 text-[13px]' : 'py-3.5 text-sm',
                    selectedOptions[card.questionData.id] !== undefined
                      ? activeQuestionActionClass
                      : (i === totalCards - 1 ? lastQuestionDisabledActionClass : disabledQuestionActionClass)
                  )}
                >
                  ثبت پاسخ
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); i === totalCards - 1 ? handleSubmit() : paginate(1); }}
                  disabled={submitting && i === totalCards - 1}
                  className={cn(
                    'w-full rounded-xl font-bold font-persian transition-all shadow-lg',
                    onboardingPreview ? 'py-2.5 text-[13px]' : 'py-3.5 text-sm',
                    activeQuestionActionClass,
                    disabledQuestionActionClass
                  )}
                >
                  {i === totalCards - 1 ? (
                    submitting ? (
                      <svg className="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      'اتمام بخش'
                    )
                  ) : (
                    'ادامه'
                  )}
                </button>
              )}
            </div>
          )}

          {isCurrent && !onboardingPreview && !isBookAnalysis && isLastCard && card.type === 'summary' && questions.length > 0 && (
            <div className="w-[72%] max-w-[280px] mt-2 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); void handleSubmit(); }}
                disabled={submitting}
                className={cn(
                  'w-full rounded-2xl font-bold font-persian transition-all shadow-xl py-3.5 text-[15px]',
                  submitting ? 'cursor-not-allowed opacity-70' : '',
                  activeQuestionActionClass,
                  disabledQuestionActionClass
                )}
              >
                {submitting ? 'در حال ارسال...' : (isBookAnalysis ? 'اتمام تحلیل' : 'تکمیل بخش')}
              </button>
            </div>
          )}

          {!onboardingPreview && isCurrent && isLastCard && card.type === 'summary' && (isBookAnalysis || (questions.length === 0 && !questionsLoading)) && (
            <div className={cn(
              "w-[85%]",
              onboardingPreview ? "mt-2" : "mt-2"
            )}>
              <button
                onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
                disabled={submitting}
                className={cn(
                  'w-full rounded-xl font-bold font-persian transition-all shadow-lg',
                  onboardingPreview ? 'py-2.5 text-[13px]' : 'py-3.5 text-sm',
                  activeQuestionActionClass,
                  disabledQuestionActionClass
                )}
              >
                {submitting ? (
                  <svg className="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  isBookAnalysis ? 'اتمام تحلیل' : 'اتمام بخش'
                )}
              </button>
            </div>
          )}

          {isCurrent && !isLastCard && !(onboardingPreview && card.type === 'summary') && (
            <div className={cn(
              "opacity-40 flex justify-center animate-slow-bounce pointer-events-none",
              onboardingPreview ? "mt-2" : "mt-2"
            )}>
              <svg className={cn(
                "opacity-70",
                onboardingPreview ? "w-8 h-8" : "w-7 h-7"
              )} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          )}
        </motion.div>
      );
    }

    return cards;
  };

  const textScrollTouchRef = useRef<{ y: number; scrollTop: number } | null>(null);
  const handleOnboardingTextTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!onboardingPreview) return;
    e.stopPropagation();
    textScrollTouchRef.current = { y: e.touches[0].clientY, scrollTop: e.currentTarget.scrollTop };
  }, [onboardingPreview]);

  const handleOnboardingTextTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!onboardingPreview || !textScrollTouchRef.current) return;
    e.stopPropagation();
    e.currentTarget.scrollTop = textScrollTouchRef.current.scrollTop +
      (textScrollTouchRef.current.y - e.touches[0].clientY);
  }, [onboardingPreview]);

  const handleOnboardingTextTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!onboardingPreview) return;
    e.stopPropagation();
    textScrollTouchRef.current = null;
  }, [onboardingPreview]);

  const renderedCards = useMemo(() => renderCards(), [
    totalCards,
    unifiedCards,
    currentCardIndex,
    onboardingPreview,
    pageBgColor,
    answered,
    selectedOptions,
    submitting,
    isBookAnalysis,
    questions.length,
    questionsLoading,
    activeQuestionActionClass,
    disabledQuestionActionClass,
    lastQuestionDisabledActionClass,
    onConfirmAnswer,
    handleSubmit,
    paginate,
    onDragEnd,
    handleOnboardingSummaryContinue,
    getOnboardingReadingFontSize,
  ]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartTimeRef.current = Date.now();
    touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    touchHandledRef.current = false;
  }, []);

  useEffect(() => {
    if (!onboardingPreview) {
      setOnboardingCardHeight(0);
      return;
    }

    const readCardHeight = () => {
      const card = document.querySelector<HTMLElement>('.current-reading-card');
      if (card) setOnboardingCardHeight(card.getBoundingClientRect().height);
    };

    readCardHeight();
    const card = document.querySelector<HTMLElement>('.current-reading-card');
    let observer: ResizeObserver | undefined;
    if (card && 'ResizeObserver' in window) {
      observer = new ResizeObserver(readCardHeight);
      observer.observe(card);
    }
    window.addEventListener('resize', readCardHeight);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', readCardHeight);
    };
  }, [onboardingPreview, currentCardIndex, questionsLoading]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touchDuration = Date.now() - touchStartTimeRef.current;
    const touchEndPos = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    const deltaX = touchEndPos.x - touchStartPosRef.current.x;
    const deltaY = touchEndPos.y - touchStartPosRef.current.y;
    const movement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]') ||
      target.closest('input') ||
      target.closest('textarea')
    ) {
      return;
    }

    const TAP_THRESHOLD = 15;
    const TIME_THRESHOLD = 400;

    if (touchDuration < TIME_THRESHOLD && movement < TAP_THRESHOLD) {
      setUiVisible((prev) => !prev);
      touchHandledRef.current = true;
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }

    setUiVisible((prev) => !prev);
  }, []);

  const bgColorClasses = {
    white: 'bg-white',
    sepia: 'bg-[#f4ecd8]',
    dark: 'bg-gray-900'
  };

  if (error) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', bgColorClasses[pageBgColor])} dir="rtl">
        <div className="text-center">
          <div className="text-4xl mb-4">!</div>
          <p className="text-muted-foreground font-persian">خطا در بارگذاری بخش</p>
          <p className="text-sm text-red-500 mt-2">{error}</p>
          <button onClick={goBack} className="mt-4 px-6 py-2 bg-primary-500 text-white rounded-lg font-persian">
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  const progressPercent = totalCards > 0 ? ((currentCardIndex + 1) / totalCards) * 100 : 0;

  return (
    <div
      className={cn('h-[100svh] w-full transition-colors duration-300 relative flex flex-col overflow-hidden fixed inset-0 touch-none', bgColorClasses[pageBgColor])}
      style={{ 
        overscrollBehavior: 'none',
        height: '100dvh',
        paddingTop: onboardingPreview ? 'var(--onboarding-header-h, 110px)' : undefined
      }}
      dir="rtl"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes slowBounce {
          0%, 100% { transform: translateY(-25%); animation-timing-function: cubic-bezier(0.8,0,1,1); }
          50% { transform: translateY(0); animation-timing-function: cubic-bezier(0,0,0.2,1); }
        }
        .animate-slow-bounce { animation: slowBounce 2.5s infinite; }
      `}</style>

      <>
        {splashState === 'intro' && (
          <ReadingSplashOverlay
            key="nonfiction-reading-intro-splash"
            text="خلاصه"
            onComplete={handleIntroSplashComplete}
          />
        )}
        {splashState === 'questions' && (
          <ReadingSplashOverlay
            key="nonfiction-reading-questions-splash"
            text="سوالات"
            onComplete={handleQuestionsSplashComplete}
          />
        )}
      </>

      {!onboardingPreview && !isBookAnalysis && splashState === 'none' && showNonfictionGuide && user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={NONFICTION_READING_GUIDE_STEPS}
        />
      )}

      {!onboardingPreview && !isBookAnalysis && splashState === 'none' && uiVisible && !isQuestionCard && user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={NONFICTION_CHAT_GUIDE_STEPS}
        />
      )}

      {!onboardingPreview && !isBookAnalysis && splashState === 'none' && isQuestionCard && currentCardIndex === summaryCardCount && user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={NONFICTION_DIFFICULTY_GUIDE_STEPS}
        />
      )}

      {!onboardingPreview && !isBookAnalysis && splashState === 'none' && isQuestionCard && currentCardIndex !== summaryCardCount && currentQuestion?.isReviewed && user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={NONFICTION_REVIEW_GUIDE_STEPS}
        />
      )}

      {!onboardingPreview && (
          <div
            className={cn(
              'fixed top-0 left-0 right-0 z-[90]',
              pageBgColor === 'dark'
                ? 'bg-gray-900'
                : pageBgColor === 'sepia'
                  ? 'bg-[#f4ecd8]'
                  : 'bg-white'
            )}
            style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem' }}
          >
            <div
              className="flex items-center gap-4 px-5 pb-1"
              style={{ paddingTop: 'calc(0.25rem + env(safe-area-inset-top))' }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); goBack(); }}
                className={cn(
                  'p-1.5 rounded-full transition-all active:scale-95',
                  pageBgColor === 'dark' ? 'hover:bg-gray-800/50 bg-gray-800 shadow-md' : 'hover:bg-gray-200/50 bg-white shadow-md border border-gray-200'
                )}
              >
                <svg className={cn('w-5 h-5', pageBgColor === 'dark' ? 'text-gray-200' : 'text-gray-700')} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <div className={cn(
                'flex-1 h-[18px] rounded-full overflow-hidden shadow-inner',
                pageBgColor === 'dark' ? 'bg-gray-800' : pageBgColor === 'sepia' ? 'bg-[#d8ccb8]' : 'bg-gray-200'
              )}>
                <motion.div
                  className={cn(
                    'h-full rounded-full',
                    pageBgColor === 'dark' ? 'bg-[#8a80ff]' : pageBgColor === 'sepia' ? 'bg-[#9a6235]' : 'bg-[#6e61ff]'
                  )}
                  initial={{ width: `${progressPercent}%` }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
              <span className={cn('text-sm font-bold font-persian min-w-[35px] text-center', pageBgColor === 'dark' ? 'text-gray-200' : 'text-gray-700')}>
                {toPersianDigits(currentCardIndex + 1)} <span className="text-xs opacity-60">از</span> {toPersianDigits(totalCards)}
              </span>
            </div>
          </div>
      )}

      <div 
        className={cn(
          'flex-1 min-h-0 relative flex w-full',
          onboardingPreview ? 'items-start justify-center pt-0' : 'items-center justify-center'
        )}
        style={{
          // The reading header is absolutely positioned, so reserve its full
          // height here to keep cards and questions from rendering underneath it.
          paddingTop: onboardingPreview ? '18px' : 'calc(3rem + env(safe-area-inset-top))',
          paddingBottom: onboardingPreview ? '10px' : 'calc(0.25rem + env(safe-area-inset-bottom))'
        }}
      >
        <MemoizedCardStack>{renderedCards}</MemoizedCardStack>
      </div>

      {onboardingPreview && !isQuestionCard && !isBookAnalysis && currentCard?.type === 'summary' && (
        <div className="relative shrink-0 z-[80] flex justify-center px-6 pt-2 pb-safe-bottom">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleOnboardingSummaryContinue(); }}
            className="w-full max-w-sm rounded-2xl bg-[#6e61ff] py-3.5 text-[15px] font-bold font-persian text-white shadow-2xl transition-transform active:scale-95"
          >
            {!questionsLoading && questions.length === 0 && hasOpenedChat ? 'اتمام بخش' : 'ادامه'}
          </button>
        </div>
      )}

      {onboardingPreview && isQuestionCard && currentQuestion && (
        <div className="relative shrink-0 z-[80] flex justify-center px-6 pt-2 pb-safe-bottom">
          {answered[currentQuestion.id] === undefined ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onConfirmAnswer(); }}
              disabled={currentSelectedOption === undefined}
              className={cn(
                'w-full max-w-sm rounded-2xl font-bold font-persian transition-all shadow-2xl py-3.5 text-[15px]',
                currentSelectedOption === undefined ? onboardingDisabledQuestionActionClass : activeQuestionActionClass,
              )}
            >
              ثبت پاسخ
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (currentCardIndex === totalCards - 1) {
                  void handleSubmit();
                } else {
                  paginate(1);
                }
              }}
              disabled={submitting && currentCardIndex === totalCards - 1}
              className={cn(
                'w-full max-w-sm rounded-2xl font-bold font-persian transition-all shadow-2xl py-3.5 text-[15px]',
                submitting ? 'cursor-not-allowed opacity-70' : '',
                activeQuestionActionClass,
                disabledQuestionActionClass,
              )}
            >
              {submitting && currentCardIndex === totalCards - 1 ? (
                <svg className="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 013 19.938l-2.647-2.647z" />
                </svg>
              ) : currentCardIndex === totalCards - 1 ? 'اتمام بخش' : 'ادامه'}
            </button>
          )}
        </div>
      )}

      {hasOpenedChat && <ReadingChat
        open={chatOpen && !isQuestionCard}
        onClose={() => setChatOpen(false)}
        contextText={currentPageContent}
        sectionId={sectionId}
        bgColor={pageBgColor}
        maxHeightVh={74}
        bottomOffset={onboardingPreview ? '4rem' : undefined}
        onboardingPreview={onboardingPreview}
        analyticsBookId={bookId}
        analyticsBookTitle={state?.bookTitle}
        analyticsSectionId={sectionId}
        analyticsFlow={onboardingPreview ? 'onboarding' : 'normal'}
        analyticsPageContext="read"
        onThinkingChange={(isThinking) => {
          if (!onboardingPreview) return;
          // وقتی کاربر پیام ارسال می‌کند و در حال فکر کردن است
          if (isThinking && guideStage >= READING_GUIDE_STAGE.CHAT_OPENED && guideStage < READING_GUIDE_STAGE.CHAT_SENT) {
            setGuideWithStage(READING_GUIDE_STAGE.CHAT_SENT, READING_ONBOARDING_GUIDE.chatSending);
          }
        }}
        onAssistantResponse={() => {
          if (!onboardingPreview) return;
          console.log('✅ [Onboarding] Assistant response received, current stage:', guideStage);
          setGuideWithStage(READING_GUIDE_STAGE.CHAT_RESPONSE, READING_ONBOARDING_GUIDE.chatResponse);
        }}
        onAssistantError={(kind) => {
          if (!onboardingPreview) return;
          console.log('❌ [Onboarding] Assistant error, current stage:', guideStage, 'kind:', kind);
          setGuideWithStage(READING_GUIDE_STAGE.CHAT_ERROR, READING_ONBOARDING_GUIDE.chatError);
        }}
      />}

      <AnimatePresence>
        {uiVisible && !isQuestionCard && !onboardingPreview && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-8 left-0 right-0 z-[60] flex justify-center"
          >
            <div className="relative flex items-center gap-4">
              <button
                onClick={(e) => { e.stopPropagation(); setShowSettingsMenu((prev) => !prev); }}
                className={cn(
                  'w-14 h-14 rounded-full shadow-2xl transition-all active:scale-90 flex items-center justify-center border',
                  pageBgColor === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-100 text-gray-600'
                )}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                data-guide="nonfiction-reading-chat-button"
                onClick={(e) => { e.stopPropagation(); logNonFictionChatClick(); setChatOpen(true); }}
                style={{ display: isBookAnalysis ? 'none' : 'flex' }}
                className="w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all active:scale-90 border bg-white border-gray-100 text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.3} d="M8 10h8M8 14h5m-9 5l1.2-3.6a8 8 0 1114.6 0L21 19l-4-1.1A8 8 0 014 19z" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
        
        {/* دکمه چت برای onboarding - استایل مشابه NovelReadingPage */}
      </AnimatePresence>

      <AnimatePresence>
        {showSettingsMenu && !onboardingPreview && (
          <ReadingSettingsMenu
            bgColor={pageBgColor}
            onBgColorChange={setPageBgColor}
            onClose={() => setShowSettingsMenu(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookReadingPage;
